/**
 * JobFilter Component
 *
 * Desktop: sidebar checkbox lists. Mobile: `chipsOnly` (active chips) or `flyout` (accordion in drawer).
 */

import React, {useLayoutEffect, useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import {defineMessages, FormattedMessage, useIntl} from 'react-intl';

import {formatJobLocation} from './format-job-location';

import {
  Accordion,
  AccordionButton,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  Checkbox,
  Divider,
  Heading,
  IconButton,
  Text,
  VStack,
  Wrap,
  WrapItem,
  useMultiStyleConfig,
} from '@salesforce/retail-react-app/app/components/shared/ui';

/** Match canonical sidebar order while still deriving values from jobs. */
const DEPARTMENT_REFERENCE_ORDER = [
  'Trade & Commercial',
  'Retail',
  'IT',
  'Supply Chain',
  'Marketing',
];

const jobFilterMessages = defineMessages({
  clearAll: {
    id: 'job_filter.button.clear_all',
    defaultMessage: 'Clear All',
  },
  departmentClassification: {
    id: 'job_filter.heading.department_classification',
    defaultMessage: 'Department Classification',
  },
  locationHeading: {
    id: 'job_filter.heading.location',
    defaultMessage: 'Location',
  },
  noDepartments: {
    id: 'job_filter.empty.no_departments',
    defaultMessage: 'No departments available',
  },
  noLocations: {
    id: 'job_filter.empty.no_locations',
    defaultMessage: 'No locations available',
  },
  removeFilterChip: {
    id: 'job_filter.aria.remove_filter',
    defaultMessage: 'Remove {name}',
  },
});

const LOCATION_REFERENCE_ORDER = [
  'Montreal, CA',
  'Calgary, Alberta, CAN',
  'Bangsar, Malaysia',
  'Portland, USA',
  'Osborne Park, WA, AU',
  'Moore Park, Sydney, NSW, AU',
  'Orange County, California, USA',
  'Design & Support Centre, Sydney, NSW, AU',
  'Newcastle, NSW, AU',
  'Bondi, Sydney, NSW, AU',
  'White City, London, UK',
];

function sortNamedOptions(names, referenceOrder) {
  const set = new Set(names);
  const ordered = [];
  for (const ref of referenceOrder) {
    if (set.has(ref)) ordered.push(ref);
  }
  const rest = [...set]
    .filter((n) => !referenceOrder.includes(n))
    .sort((a, b) => a.localeCompare(b));
  return [...ordered, ...rest].map((name) => ({name}));
}

/** @param {{jobs?: Array, filters?: Object, onFilterChange?: Function, variant?: 'full' | 'chipsOnly' | 'flyout', drawerIsOpen?: boolean}} props */
const JobFilter = ({
  jobs = [],
  filters = {},
  onFilterChange,
  variant = 'full',
  drawerIsOpen,
}) => {
  const intl = useIntl();
  const boardTheme = useMultiStyleConfig('CareersJobBoard', {});
  const [flyoutExpandedIdx, setFlyoutExpandedIdx] = useState(0);

  useLayoutEffect(() => {
    if (variant === 'flyout' && drawerIsOpen) {
      setFlyoutExpandedIdx(0);
    }
  }, [drawerIsOpen, variant]);

  const filterOptions = useMemo(() => {
    const locationCounts = new Map();
    const departmentCounts = new Map();

    jobs.forEach((job) => {
      if (job.location) {
        const label = formatJobLocation(job.location);
        if (label) {
          locationCounts.set(label, (locationCounts.get(label) || 0) + 1);
        }
      }

      const dept = job.category || job.department;
      if (dept) {
        departmentCounts.set(dept, (departmentCounts.get(dept) || 0) + 1);
      }
    });

    const departmentNames = sortNamedOptions([...departmentCounts.keys()], DEPARTMENT_REFERENCE_ORDER);
    const locationNames = sortNamedOptions([...locationCounts.keys()], LOCATION_REFERENCE_ORDER);

    return {
      locations: locationNames.filter(({name}) => locationCounts.has(name)),
      departments: departmentNames.filter(({name}) => departmentCounts.has(name)),
    };
  }, [jobs]);

  const handleDepartmentChange = (dept, isChecked) => {
    const currentDepts = filters.departments || [];
    let newDepts;

    if (isChecked) {
      newDepts = [...currentDepts, dept];
    } else {
      newDepts = currentDepts.filter((d) => d !== dept);
    }

    onFilterChange?.({
      ...filters,
      departments: newDepts.length > 0 ? newDepts : undefined,
    });
  };

  const handleLocationChange = (location, isChecked) => {
    const currentLocations = filters.locations || [];
    let newLocations;

    if (isChecked) {
      newLocations = [...currentLocations, location];
    } else {
      newLocations = currentLocations.filter((l) => l !== location);
    }

    onFilterChange?.({
      ...filters,
      locations: newLocations.length > 0 ? newLocations : undefined,
    });
  };

  const handleClear = () => {
    onFilterChange?.({});
  };

  const removeDepartmentChip = (dept) => {
    const next = (filters.departments || []).filter((d) => d !== dept);
    onFilterChange?.({
      ...filters,
      departments: next.length > 0 ? next : undefined,
    });
  };

  const removeLocationChip = (loc) => {
    const next = (filters.locations || []).filter((l) => l !== loc);
    onFilterChange?.({
      ...filters,
      locations: next.length > 0 ? next : undefined,
    });
  };

  const hasActiveFilters =
    (filters.departments && filters.departments.length > 0) ||
    (filters.locations && filters.locations.length > 0);

  const activeChipsSection = hasActiveFilters ? (
    <Box {...boardTheme.filterActiveWrap}>
      <Wrap spacing={2}>
        {(filters.departments || []).map((dept) => (
          <WrapItem key={`dept-${dept}`}>
            <Box {...boardTheme.filterChipOuter}>
              <Text {...boardTheme.filterChipLabel}>{dept}</Text>
              <IconButton
                {...boardTheme.filterChipRemove}
                aria-label={intl.formatMessage(jobFilterMessages.removeFilterChip, {name: dept})}
                icon={<FilterChipCloseIcon />}
                onClick={() => removeDepartmentChip(dept)}
              />
            </Box>
          </WrapItem>
        ))}
        {(filters.locations || []).map((loc) => (
          <WrapItem key={`loc-${loc}`}>
            <Box {...boardTheme.filterChipOuter}>
              <Text {...boardTheme.filterChipLabel}>{loc}</Text>
              <IconButton
                {...boardTheme.filterChipRemove}
                aria-label={intl.formatMessage(jobFilterMessages.removeFilterChip, {name: loc})}
                icon={<FilterChipCloseIcon />}
                onClick={() => removeLocationChip(loc)}
              />
            </Box>
          </WrapItem>
        ))}
      </Wrap>
      <Button {...boardTheme.filterClearAll} onClick={handleClear}>
        <FormattedMessage {...jobFilterMessages.clearAll} />
      </Button>
    </Box>
  ) : null;

  if (variant === 'chipsOnly') {
    return activeChipsSection ?? null;
  }

  if (variant === 'flyout') {
    return (
      <Accordion
        {...boardTheme.filterFlyoutAccordion}
        allowToggle
        index={flyoutExpandedIdx}
        reduceMotion
        onChange={setFlyoutExpandedIdx}
      >
        <AccordionItem {...boardTheme.filterFlyoutAccordionItem}>
          {({isExpanded}) => (
            <>
              <AccordionButton {...boardTheme.filterFlyoutAccordionButton}>
                <Box
                  {...boardTheme.filterFlyoutAccordionTitle}
                  textDecoration={isExpanded ? 'underline' : 'none'}
                  textDecorationColor={isExpanded ? '#000' : undefined}
                  textUnderlineOffset={isExpanded ? '3px' : undefined}
                >
                  <FormattedMessage {...jobFilterMessages.departmentClassification} />
                </Box>
                <Text {...boardTheme.filterFlyoutExpandGlyph} as="span" aria-hidden>
                  {isExpanded ? '\u2212' : '+'}
                </Text>
              </AccordionButton>
              <AccordionPanel {...boardTheme.filterFlyoutAccordionPanel}>
                <VStack {...boardTheme.filterFlyoutCheckboxStack}>
                  {filterOptions.departments.length > 0 ? (
                    filterOptions.departments.map(({name}) => (
                      <Checkbox
                        key={name}
                        isChecked={(filters.departments || []).includes(name)}
                        size="md"
                        spacing={3}
                        {...boardTheme.filterCheckbox}
                        onChange={(e) => handleDepartmentChange(name, e.target.checked)}
                      >
                        <Text {...boardTheme.filterCheckboxLabelText}>{name}</Text>
                      </Checkbox>
                    ))
                  ) : (
                    <Text {...boardTheme.filterMutedText}>
                      <FormattedMessage {...jobFilterMessages.noDepartments} />
                    </Text>
                  )}
                </VStack>
              </AccordionPanel>
            </>
          )}
        </AccordionItem>

        <AccordionItem {...boardTheme.filterFlyoutAccordionItem}>
          {({isExpanded}) => (
            <>
              <AccordionButton {...boardTheme.filterFlyoutAccordionButton}>
                <Box
                  {...boardTheme.filterFlyoutAccordionTitle}
                  textDecoration={isExpanded ? 'underline' : 'none'}
                  textDecorationColor={isExpanded ? '#000' : undefined}
                  textUnderlineOffset={isExpanded ? '3px' : undefined}
                >
                  <FormattedMessage {...jobFilterMessages.locationHeading} />
                </Box>
                <Text {...boardTheme.filterFlyoutExpandGlyph} as="span" aria-hidden>
                  {isExpanded ? '\u2212' : '+'}
                </Text>
              </AccordionButton>
              <AccordionPanel {...boardTheme.filterFlyoutAccordionPanel}>
                <VStack {...boardTheme.filterFlyoutCheckboxStack}>
                  {filterOptions.locations.length > 0 ? (
                    filterOptions.locations.map(({name}) => (
                      <Checkbox
                        key={name}
                        isChecked={(filters.locations || []).includes(name)}
                        size="md"
                        spacing={3}
                        {...boardTheme.filterCheckbox}
                        onChange={(e) => handleLocationChange(name, e.target.checked)}
                      >
                        <Text {...boardTheme.filterCheckboxLabelText}>{name}</Text>
                      </Checkbox>
                    ))
                  ) : (
                    <Text {...boardTheme.filterMutedText}>
                      <FormattedMessage {...jobFilterMessages.noLocations} />
                    </Text>
                  )}
                </VStack>
              </AccordionPanel>
            </>
          )}
        </AccordionItem>
      </Accordion>
    );
  }

  return (
    <Box>
      {activeChipsSection}

      <Box {...boardTheme.filterSectionWrap}>
        <Heading {...boardTheme.filterSectionHeading}>
          <FormattedMessage {...jobFilterMessages.departmentClassification} />
        </Heading>
        <VStack {...boardTheme.filterCheckboxList}>
          {filterOptions.departments.length > 0 ? (
            filterOptions.departments.map(({name}) => (
              <Checkbox
                key={name}
                isChecked={(filters.departments || []).includes(name)}
                size="md"
                spacing={3}
                {...boardTheme.filterCheckbox}
                onChange={(e) => handleDepartmentChange(name, e.target.checked)}
              >
                <Text {...boardTheme.filterCheckboxLabelText}>{name}</Text>
              </Checkbox>
            ))
          ) : (
            <Text {...boardTheme.filterMutedText}>
              <FormattedMessage {...jobFilterMessages.noDepartments} />
            </Text>
          )}
        </VStack>
      </Box>

      <Divider {...boardTheme.filterSectionDivider} />

      <Box {...boardTheme.filterLocationOuter}>
        <Heading {...boardTheme.filterSectionHeading}>
          <FormattedMessage {...jobFilterMessages.locationHeading} />
        </Heading>
        <VStack {...boardTheme.filterLocationsScroll}>
          {filterOptions.locations.length > 0 ? (
            filterOptions.locations.map(({name}) => (
              <Checkbox
                key={name}
                isChecked={(filters.locations || []).includes(name)}
                size="md"
                spacing={3}
                {...boardTheme.filterCheckbox}
                onChange={(e) => handleLocationChange(name, e.target.checked)}
              >
                <Text {...boardTheme.filterCheckboxLabelText}>{name}</Text>
              </Checkbox>
            ))
          ) : (
            <Text {...boardTheme.filterMutedText}>
              <FormattedMessage {...jobFilterMessages.noLocations} />
            </Text>
          )}
        </VStack>
      </Box>
    </Box>
  );
};

function FilterChipCloseIcon() {
  return (
    <svg aria-hidden fill="none" height="13" viewBox="0 0 24 24" width="13">
      <path
        d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"
        fill="currentColor"
      />
    </svg>
  );
}

JobFilter.propTypes = {
  jobs: PropTypes.array,
  filters: PropTypes.shape({
    departments: PropTypes.arrayOf(PropTypes.string),
    locations: PropTypes.arrayOf(PropTypes.string),
  }),
  onFilterChange: PropTypes.func,
  variant: PropTypes.oneOf(['full', 'chipsOnly', 'flyout']),
  drawerIsOpen: PropTypes.bool,
};

export default JobFilter;
